import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { journalApi } from '../../api/journal';
import { formatMoney } from '../../utils/formatMoney';
import { formatDate } from '../../utils/formatDate';
import LoadingSpinner from '../../components/LoadingSpinner';
import ConfirmDialog from '../../components/ConfirmDialog';
import { useToast } from '../../components/Toast';
import { useState } from 'react';
import { useIsMirror } from '../../hooks/useIsMirror';
import type { JournalEntryDto } from '../../types/journal';

export default function JournalDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showCloneDialog, setShowCloneDialog] = useState(false);
  // ADR-004: mirror workspaces (mysql, asmx-owned) are READ-ONLY — hide the
  // write affordances; the API 403s them anyway (defense in depth).
  const isMirror = useIsMirror();

  const cloneMutation = useMutation({
    mutationFn: () => journalApi.clone(Number(id), {
      newDate: new Date().toISOString().slice(0, 10),
    }),
    onSuccess: (newId) => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      navigate(`/journal/${newId}`);
    },
  });

  const { data: entry, isLoading } = useQuery<JournalEntryDto>({
    queryKey: ['journal-entry', id],
    queryFn: () => journalApi.getById(Number(id)),
    enabled: !!id,
  });

  const deleteMutation = useMutation({
    mutationFn: () => journalApi.delete(Number(id)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['journal-entries'] });
      addToast(`Žurnalo įrašas Nr. ${entry?.documentNumber || id} ištrintas`, 'success');
      navigate('/journal');
    },
  });

  if (isLoading) return <LoadingSpinner />;
  if (!entry) return <div className="text-center py-8 text-gray-500">Įrašas nerastas</div>;

  const balanced = Math.abs(entry.totalDebit - entry.totalCredit) < 0.005;

  return (
    <div className="space-y-4 max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">
            Žurnalo įrašas Nr. {entry.documentNumber}
          </h1>
          <p className="text-sm text-gray-500">ID: {entry.id} | Tipas: {entry.documentType}</p>
        </div>
        <div className="flex gap-2">
          {!isMirror && (
            <>
              <button
                className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
                onClick={() => navigate(`/journal/${entry.id}/edit`)}
              >
                Redaguoti
              </button>
              <button
                className="px-3 py-1.5 text-sm bg-primary-100 text-primary-700 rounded-md hover:bg-primary-200"
                onClick={() => setShowCloneDialog(true)}
              >
                Kopijuoti
              </button>
              <button
                className="px-3 py-1.5 text-sm bg-red-100 text-red-700 rounded-md hover:bg-red-200"
                onClick={() => setShowDeleteDialog(true)}
              >
                Ištrinti
              </button>
            </>
          )}
          <button
            className="px-3 py-1.5 text-sm bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200"
            onClick={() => navigate(-1)}
          >
            Atgal
          </button>
        </div>
      </div>

      {/* Entry details */}
      <div className="bg-white rounded-lg border border-gray-200 p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <div className="text-gray-500 text-xs">Data</div>
            <div className="font-medium">{formatDate(entry.date)}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Dokumento nr.</div>
            <div className="font-medium">{entry.documentNumber}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Dokumento tipas</div>
            <div className="font-medium">{entry.documentType}</div>
          </div>
          <div>
            <div className="text-gray-500 text-xs">Asmuo</div>
            <div className="font-medium">{entry.personName ?? '-'}</div>
          </div>
        </div>
        <div className="mt-3">
          <div className="text-gray-500 text-xs">Turinys</div>
          <div className="text-sm">{entry.content}</div>
        </div>
        <div className="mt-3 flex gap-4">
          <div className={`text-sm font-medium ${balanced ? 'text-green-600' : 'text-red-600'}`}>
            {balanced ? 'Subalansuota' : 'NESUBALANSUOTA'}
          </div>
          <div className="text-sm">
            Debetas: <span className="num font-semibold">{formatMoney(entry.totalDebit)}</span>
          </div>
          <div className="text-sm">
            Kreditas: <span className="num font-semibold">{formatMoney(entry.totalCredit)}</span>
          </div>
        </div>
      </div>

      {/* Lines */}
      <div className="bg-white rounded-lg border border-gray-200">
        <div className="px-4 py-2 border-b border-gray-200">
          <h2 className="font-semibold text-sm">Eilutės</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="dense w-full">
            <thead>
              <tr>
                <th className="text-right">Sąskaita</th>
                <th className="text-left">Sąskaitos pavadinimas</th>
                <th className="text-right">Debetas</th>
                <th className="text-right">Kreditas</th>
                <th className="text-left">Asmuo</th>
              </tr>
            </thead>
            <tbody>
              {entry.lines.map((line) => (
                <tr key={line.id}>
                  <td className="num">{line.account}</td>
                  <td>{line.accountName || '-'}</td>
                  <td className="num">
                    {line.entryType === 'Debetas' ? formatMoney(line.amount) : ''}
                  </td>
                  <td className="num">
                    {line.entryType === 'Kreditas' ? formatMoney(line.amount) : ''}
                  </td>
                  <td>{line.personName ?? (line.personId ? `Asmuo ${line.personId}` : '-')}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={2} className="text-right font-semibold">Viso</td>
                <td className="num font-semibold">{formatMoney(entry.totalDebit)}</td>
                <td className="num font-semibold">{formatMoney(entry.totalCredit)}</td>
                <td></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Source invoice lines (goods/services) — derived from the linked invoice */}
      {entry.sourceInvoice && entry.sourceInvoice.lines.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200">
          <div className="px-4 py-2 border-b border-gray-200 flex items-center justify-between">
            <h2 className="font-semibold text-sm">
              Sąskaitos faktūros eilutės
              <span className="ml-2 text-gray-500 font-normal">
                {entry.sourceInvoice.direction === 'Made' ? 'Pardavimo' : 'Pirkimo'} SF
                Nr. {entry.sourceInvoice.documentNumber}
              </span>
            </h2>
            <button
              className="px-3 py-1.5 text-sm bg-primary-50 text-primary-700 rounded-md hover:bg-primary-100"
              onClick={() => navigate(
                `/invoices/${entry.sourceInvoice!.id}?direction=${entry.sourceInvoice!.direction}`
              )}
            >
              Atidaryti sąskaitą
            </button>
          </div>
          <div className="overflow-x-auto">
            <table className="dense w-full">
              <thead>
                <tr>
                  <th className="text-left">Pavadinimas</th>
                  <th className="text-right">Kiekis</th>
                  <th className="text-left">Mat. vnt.</th>
                  <th className="text-right">Vnt. kaina</th>
                  <th className="text-right">PVM %</th>
                  <th className="text-right">Suma</th>
                  <th className="text-right">PVM</th>
                  <th className="text-right">Iš viso</th>
                  <th className="text-right">Sąskaita</th>
                </tr>
              </thead>
              <tbody>
                {entry.sourceInvoice.lines.map((line) => (
                  <tr key={line.id}>
                    <td>{line.name}</td>
                    <td className="num">{line.quantity}</td>
                    <td>{line.unit || '-'}</td>
                    <td className="num">{formatMoney(line.unitPrice)}</td>
                    <td className="num">{line.vatRate ? `${(line.vatRate * 100).toFixed(0)}%` : '-'}</td>
                    <td className="num">{formatMoney(line.sum)}</td>
                    <td className="num">{formatMoney(line.sumVat)}</td>
                    <td className="num">{formatMoney(line.sum + line.sumVat)}</td>
                    <td className="num">{line.account}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <td colSpan={5} className="text-right font-semibold">Viso</td>
                  <td className="num font-semibold">{formatMoney(entry.sourceInvoice.totalSum)}</td>
                  <td className="num font-semibold">{formatMoney(entry.sourceInvoice.totalVat)}</td>
                  <td className="num font-semibold">
                    {formatMoney(entry.sourceInvoice.totalSum + entry.sourceInvoice.totalVat)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </div>
      )}

      {/* Delete dialog */}
      <ConfirmDialog
        open={showDeleteDialog}
        title="Ištrinti žurnalo įrašą"
        message={`Negrįžtamai ištrinti žurnalo įrašą Nr. ${entry.documentNumber}? Šio veiksmo atšaukti negalima.`}
        confirmLabel="Ištrinti"
        onConfirm={() => deleteMutation.mutate()}
        onCancel={() => setShowDeleteDialog(false)}
      />

      {/* Clone dialog */}
      <ConfirmDialog
        open={showCloneDialog}
        title="Kopijuoti žurnalo įrašą"
        message={`Sukurti žurnalo įrašo Nr. ${entry.documentNumber} kopiją su šiandienos data?`}
        confirmLabel="Kopijuoti"
        onConfirm={() => cloneMutation.mutate()}
        onCancel={() => setShowCloneDialog(false)}
      />
    </div>
  );
}
